import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { History, ChevronDown, ChevronRight, Clock, User, Bot, Loader2 } from 'lucide-react';

const money = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const num = new Intl.NumberFormat('es-AR');

export const fmtFechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  timeZone: 'America/Argentina/Buenos_Aires',
}) : '—');

export const fmtDuracion = (s) => {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${Math.round(s)} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
};

export const ESTADOS = {
  ok: { l: 'OK', cls: 'bg-emerald-100 text-emerald-700' },
  error: { l: 'Error', cls: 'bg-rose-100 text-rose-700' },
  en_ejecucion: { l: 'En curso', cls: 'bg-sky-100 text-sky-700' },
  omitida: { l: 'Omitida', cls: 'bg-slate-100 text-slate-600' },
  sin_datos: { l: 'Sin novedades', cls: 'bg-slate-100 text-slate-600' },
  interrumpida: { l: 'Interrumpida', cls: 'bg-rose-50 text-rose-600' },
};

export const origenLabel = (r) => ({
  cron: 'Automática 01:00', manual: `Manual${r.usuario ? ` · ${r.usuario}` : ''}`,
  programada: 'Programada', grupo: 'Grupo de automatizaciones', anterior: 'Registro anterior',
}[r.origen] || r.origen || '—');

export const EstadoBadge = ({ status, testid }) => {
  const e = ESTADOS[status] || { l: status || '—', cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`${e.cls} inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold`} data-testid={testid}>
      {status === 'en_ejecucion' && <Loader2 size={11} className="animate-spin" />}{e.l}
    </span>
  );
};

const Lista = ({ titulo, items, render, testid }) => (items?.length ? (
  <div data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{titulo} ({items.length})</p>
    <div className="max-h-40 overflow-y-auto rounded-md border border-slate-100 divide-y divide-slate-100 bg-white">
      {items.map((x, i) => (
        <div key={`${x.prestamo_id}-${i}`} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs">
          <span className="truncate text-slate-700"><span className="font-mono text-slate-400">#{x.prestamo_id}</span> {x.nombre} <span className="text-slate-400">{x.cuit}</span></span>
          <span className="shrink-0 text-slate-600">{render(x)}</span>
        </div>
      ))}
    </div>
  </div>
) : null);

const Corrida = ({ r, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  const c = r.cambios;
  const tieneDetalle = (r.metricas?.length || 0) > 0 || c || r.error;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`sync-run-${r.id}`}>
      <button type="button" onClick={() => tieneDetalle && setOpen((v) => !v)}
        className={`w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left ${tieneDetalle ? '' : 'cursor-default'}`} data-testid={`sync-run-toggle-${r.id}`}>
        {tieneDetalle ? (open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />) : <span className="w-[15px]" />}
        <span className="text-sm font-semibold text-slate-800 tabular-nums">{fmtFechaHora(r.started_at)}</span>
        <EstadoBadge status={r.status} testid={`sync-run-estado-${r.id}`} />
        <span className="flex items-center gap-1 text-xs text-slate-500">
          {r.origen === 'manual' ? <User size={12} /> : <Bot size={12} />} {origenLabel(r)}
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-400"><Clock size={12} /> {fmtDuracion(r.duracion_seg)}</span>
        {r.resumen && <p className="basis-full pl-[27px] text-xs text-slate-500">{r.resumen}</p>}
      </button>
      {open && tieneDetalle && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 space-y-3">
          {r.error && <p className="text-xs text-rose-600" data-testid={`sync-run-error-${r.id}`}>{r.error}</p>}
          {r.metricas?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {r.metricas.map((m) => (
                <div key={m.label} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{m.label}</p>
                  <p className="text-sm font-bold tabular-nums text-slate-800">{m.tipo === 'money' ? money.format(m.valor || 0) : num.format(m.valor || 0)}</p>
                </div>
              ))}
            </div>
          )}
          {c && (
            <div className="grid gap-3 md:grid-cols-2">
              <Lista titulo="Préstamos nuevos" items={c.nuevos} testid={`sync-run-nuevos-${r.id}`} render={(x) => `${x.estado || ''} · ${x.monto || ''}`} />
              <Lista titulo="Cambios de estado" items={c.cambios_estado} testid={`sync-run-cambios-${r.id}`} render={(x) => `${x.de || '—'} → ${x.a || '—'}`} />
              <Lista titulo="Ya no vinieron en el rango" items={c.salieron} testid={`sync-run-salieron-${r.id}`} render={(x) => x.estado || ''} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SyncHistorialDialog = ({ open, onOpenChange, data }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="sync-historial-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><History size={18} className="text-teal-600" /> Historial de actualizaciones desde Waya</DialogTitle>
        <DialogDescription>
          {data?.activa ? `${data.programacion} · además del botón "Actualizar desde Waya".` : 'La actualización automática está desactivada (Configuración de KPIs).'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        {(data?.items || []).map((r, i) => <Corrida key={r.id} r={r} idx={i} />)}
        {!data?.items?.length && <p className="text-sm text-slate-500 py-6 text-center" data-testid="sync-historial-vacio">Todavía no hay corridas registradas.</p>}
      </div>
    </DialogContent>
  </Dialog>
);

export default SyncHistorialDialog;
